import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFortuneData } from '../contexts/FortuneContext';
import { getProductById } from '../data/products';
import '../styles/BirthInputPage.css';

const BirthInputPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    selectedProduct, 
    setSelectedProduct,
    userBirthInfo, 
    setUserBirthInfo, 
    partnerBirthInfo, 
    setPartnerBirthInfo 
  } = useFortuneData();

  const [errors, setErrors] = useState({});
  const [requiresPartner, setRequiresPartner] = useState(false);

  useEffect(() => {
    // URL 파라미터에서 상품 ID와 상대방 정보 필요 여부 가져오기
    const params = new URLSearchParams(location.search);
    const productId = params.get('product');
    const partnerRequired = params.get('requiresPartner') === 'true';
    
    // URL에서 직접 requiresPartner 값을 가져와서 설정
    if (partnerRequired) {
      setRequiresPartner(true);
    }
    
    // 상품 ID가 있으면 해당 상품 정보 가져오기
    if (productId && !selectedProduct) {
      const product = getProductById(parseInt(productId));
      if (product) {
        setSelectedProduct(product);
        // URL에서 requiresPartner 값이 명시적으로 전달된 경우 그 값을 우선 사용
        if (params.get('requiresPartner') === null) {
          setRequiresPartner(product.requiresPartner);
        }
      } else {
        navigate('/');
      }
    } else if (!selectedProduct) {
      // 상품이 없으면 홈으로 리디렉션
      navigate('/');
    } else if (params.get('requiresPartner') === null) {
      // URL에 requiresPartner 값이 없고 선택된 상품이 있을 경우 상품의 값 사용
      setRequiresPartner(selectedProduct.requiresPartner);
    }
  }, [selectedProduct, navigate, location.search, setSelectedProduct]);

  const handleUserBirthChange = (e) => {
    const { name, value } = e.target;
    setUserBirthInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePartnerBirthChange = (e) => {
    const { name, value } = e.target;
    setPartnerBirthInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUnknownTimeChange = (e) => {
    const { checked } = e.target;
    setUserBirthInfo(prev => ({
      ...prev,
      unknownTime: checked,
      hour: checked ? '' : prev.hour,
      minute: checked ? '' : prev.minute
    }));
  };

  const handlePartnerUnknownTimeChange = (e) => {
    const { checked } = e.target;
    setPartnerBirthInfo(prev => ({
      ...prev,
      unknownTime: checked,
      hour: checked ? '' : prev.hour,
      minute: checked ? '' : prev.minute
    }));
  };

  const validateUserBirthInfo = () => {
    const newErrors = {};
    
    if (!userBirthInfo.year || !userBirthInfo.month || !userBirthInfo.day) {
      newErrors.userBirth = t('validate_birth_date');
    }
    
    if (!userBirthInfo.unknownTime && (!userBirthInfo.hour || !userBirthInfo.minute)) {
      newErrors.userTime = t('validate_birth_time');
    }
    
    return newErrors;
  };

  const validatePartnerBirthInfo = () => {
    const newErrors = {};
    
    if (!partnerBirthInfo.year || !partnerBirthInfo.month || !partnerBirthInfo.day) {
      newErrors.partnerBirth = t('validate_partner_birth');
    }
    
    return newErrors;
  };

  const handleNext = () => {
    let validationErrors = validateUserBirthInfo();
    
    if (requiresPartner) {
      const partnerErrors = validatePartnerBirthInfo();
      validationErrors = { ...validationErrors, ...partnerErrors };
    }
    
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      navigate('/payment');
    }
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 100; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  const generateMonthOptions = () => {
    const months = [];
    for (let month = 1; month <= 12; month++) {
      months.push(month);
    }
    return months;
  };

  const generateDayOptions = () => {
    const days = [];
    for (let day = 1; day <= 31; day++) {
      days.push(day);
    }
    return days;
  };

  const generateHourOptions = () => {
    const hours = [];
    for (let hour = 0; hour < 24; hour++) {
      hours.push(hour);
    }
    return hours;
  };

  const generateMinuteOptions = () => {
    const minutes = [];
    for (let minute = 0; minute < 60; minute++) {
      minutes.push(minute);
    }
    return minutes;
  };

  return (
    <div className="birth-input-page">

      <div className="birth-input-content">
        <div className="user-birth-section">
          <h2 className="section-title">{t('birth_info_title')}</h2>
          <p className="section-desc">{t('birth_info_desc')}</p>
          
          <div className="form-group">
            <label className="form-label">{t('birth_date')} <span className="required">*</span></label>
            <div className="date-inputs">
              <select 
                name="year" 
                value={userBirthInfo.year} 
                onChange={handleUserBirthChange}
                className="date-select"
              >
                <option value="">{t('birth_year')}</option>
                {generateYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              
              <select 
                name="month" 
                value={userBirthInfo.month} 
                onChange={handleUserBirthChange}
                className="date-select"
              >
                <option value="">{t('birth_month')}</option>
                {generateMonthOptions().map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
              
              <select 
                name="day" 
                value={userBirthInfo.day} 
                onChange={handleUserBirthChange}
                className="date-select"
              >
                <option value="">{t('birth_day')}</option>
                {generateDayOptions().map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            {errors.userBirth && <p className="error-message">{errors.userBirth}</p>}
          </div>
          
          <div className="form-group">
            <label className="form-label">{t('birth_time')}</label>
            <div className="time-inputs">
              <select 
                name="hour" 
                value={userBirthInfo.hour} 
                onChange={handleUserBirthChange}
                className="time-select"
                disabled={userBirthInfo.unknownTime}
              >
                <option value="">{t('hour')}</option>
                {generateHourOptions().map(hour => (
                  <option key={hour} value={hour}>{hour.toString().padStart(2, '0')}</option>
                ))}
              </select>
              
              <select 
                name="minute" 
                value={userBirthInfo.minute} 
                onChange={handleUserBirthChange}
                className="time-select"
                disabled={userBirthInfo.unknownTime}
              >
                <option value="">{t('minute')}</option>
                {generateMinuteOptions().map(minute => (
                  <option key={minute} value={minute}>{minute.toString().padStart(2, '0')}</option>
                ))}
              </select>
              
              <div className="unknown-time-checkbox">
                <input 
                  type="checkbox" 
                  id="unknownTime" 
                  checked={userBirthInfo.unknownTime} 
                  onChange={handleUnknownTimeChange}
                />
                <label htmlFor="unknownTime">{t('unknown_time')}</label>
              </div>
            </div>
            {errors.userTime && <p className="error-message">{errors.userTime}</p>}
          </div>
        </div>
        
        {requiresPartner && (
          <div className="partner-birth-section">
            <h2 className="section-title">{t('partner_info')}</h2>
            {/* <p className="section-desc">{selectedProduct ? `${selectedProduct.nameKey ? (selectedProduct.nameKey) : selectedProduct.name} ${t('requires_partner_info')}` : t('requires_partner_info')}</p> */}
            
            <div className="form-group">
              <label className="form-label">{t('partner_birth_date')} <span className="required">*</span></label>
              <div className="date-inputs">
                <select 
                  name="year" 
                  value={partnerBirthInfo.year} 
                  onChange={handlePartnerBirthChange}
                  className="date-select"
                >
                  <option value="">{t('birth_year')}</option>
                  {generateYearOptions().map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                
                <select 
                  name="month" 
                  value={partnerBirthInfo.month} 
                  onChange={handlePartnerBirthChange}
                  className="date-select"
                >
                  <option value="">{t('birth_month')}</option>
                  {generateMonthOptions().map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                
                <select 
                  name="day" 
                  value={partnerBirthInfo.day} 
                  onChange={handlePartnerBirthChange}
                  className="date-select"
                >
                  <option value="">{t('birth_day')}</option>
                  {generateDayOptions().map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
              {errors.partnerBirth && <p className="error-message">{errors.partnerBirth}</p>}
            </div>
            
            <div className="form-group">
              <label className="form-label">{t('birth_time')}</label>
              <div className="time-inputs">
                <select 
                  name="hour" 
                  value={partnerBirthInfo.hour} 
                  onChange={handlePartnerBirthChange}
                  className="time-select"
                  disabled={partnerBirthInfo.unknownTime}
                >
                  <option value="">{t('hour')}</option>
                  {generateHourOptions().map(hour => (
                    <option key={hour} value={hour}>{hour.toString().padStart(2, '0')}</option>
                  ))}
                </select>
                
                <select 
                  name="minute" 
                  value={partnerBirthInfo.minute} 
                  onChange={handlePartnerBirthChange}
                  className="time-select"
                  disabled={partnerBirthInfo.unknownTime}
                >
                  <option value="">{t('minute')}</option>
                  {generateMinuteOptions().map(minute => (
                    <option key={minute} value={minute}>{minute.toString().padStart(2, '0')}</option>
                  ))}
                </select>
                
                <div className="unknown-time-checkbox">
                  <input 
                    type="checkbox" 
                    id="partnerUnknownTime" 
                    checked={partnerBirthInfo.unknownTime} 
                    onChange={handlePartnerUnknownTimeChange}
                  />
                  <label htmlFor="partnerUnknownTime">{t('unknown_time')}</label>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <button className="next-button" onClick={handleNext}>
          {requiresPartner ? t('payment') : t('next')}
        </button>
      </div>
    </div>
  );
};

export default BirthInputPage;
